/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faTimes } from '@fortawesome/free-solid-svg-icons';
import { MEDICAL_FILE_CATEGORY, CLINICAL_TYPE } from '@shafa/shared';
import { post } from '../../../utils/fetchAPI';
import { showErrorToast, showSuccessToast } from '../../../utils/toaster-alert';
import CustomModal from '../../common/modal/modal';

/** Must stay in sync with API `maxFilesCount` (multer `files` limit). */
const MAX_FILES_PER_UPLOAD = 5;

function formatFileSize(bytes) {
    if (bytes == null || Number.isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function sameFile(a, b) {
    return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

const PatientFilesUploadModal = ({
    show,
    onClose,
    onUploaded,
    organizationId,
    locationId,
    patientId,
    visitId,
    missingContext,
}) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState(MEDICAL_FILE_CATEGORY.OTHER);
    const [clinicalType, setClinicalType] = useState(CLINICAL_TYPE.OTHER);
    const [fileList, setFileList] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const formRef = useRef(null);
    const fileInputRef = useRef(null);

    const categoryOptions = useMemo(() => Object.entries(MEDICAL_FILE_CATEGORY), []);
    const clinicalTypeOptions = useMemo(() => Object.entries(CLINICAL_TYPE), []);

    const resetForm = useCallback(() => {
        setTitle('');
        setDescription('');
        setCategory(MEDICAL_FILE_CATEGORY.OTHER);
        setClinicalType(CLINICAL_TYPE.OTHER);
        setFileList([]);
        formRef.current?.reset();
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const handleSetShow = (next) => {
        if (next === false) {
            resetForm();
        }
        if (!next) {
            onClose();
        }
    };

    const imageBlobUrls = useMemo(
        () =>
            fileList.map((f) =>
                f.type && f.type.startsWith('image/') ? URL.createObjectURL(f) : null
            ),
        [fileList]
    );

    useEffect(() => {
        return () => {
            imageBlobUrls.forEach((u) => u && URL.revokeObjectURL(u));
        };
    }, [imageBlobUrls]);

    const onFileChange = (e) => {
        const raw = e.target.files ? Array.from(e.target.files) : [];
        // Ignore spurious empty change events (modal focus / strict mode) that wipe a multi-select.
        if (raw.length === 0) {
            return;
        }

        setFileList((prev) => {
            // Multi-select in one dialog → replace selection (cap at max)
            if (raw.length > 1) {
                if (raw.length > MAX_FILES_PER_UPLOAD) {
                    showErrorToast(
                        `You can upload at most ${MAX_FILES_PER_UPLOAD} files at once. Only the first ${MAX_FILES_PER_UPLOAD} were kept.`
                    );
                }
                return raw.slice(0, MAX_FILES_PER_UPLOAD);
            }

            // Single file → append (lets users add files in multiple steps)
            const f = raw[0];
            if (prev.some((p) => sameFile(p, f))) {
                return prev;
            }
            if (prev.length >= MAX_FILES_PER_UPLOAD) {
                showErrorToast(`You can upload at most ${MAX_FILES_PER_UPLOAD} files.`);
                return prev;
            }
            return [...prev, f];
        });

        // Allow picking the same file name again on a later click
        e.target.value = '';
    };

    const removeFileAt = (index) => {
        setFileList((prev) => prev.filter((_, i) => i !== index));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (missingContext) {
            showErrorToast('Missing organization, location, patient, or visit.');
            return;
        }
        if (!fileList.length) {
            showErrorToast('Select at least one file (PDF or image).');
            return;
        }

        const filesToSend = fileList.slice(0, MAX_FILES_PER_UPLOAD);

        const formData = new FormData();
        formData.append('organization', organizationId);
        formData.append('location', locationId);
        if (visitId) {
            formData.append('visitId', String(visitId));
        }
        formData.append('title', title.trim());
        formData.append('description', description.trim());
        formData.append('category', category);
        formData.append('clinicalType', clinicalType);
        filesToSend.forEach((f) => formData.append('files', f));

        setSubmitting(true);
        try {
            const res = await post(`patients/${patientId}/medical-files`, formData);
            if (res?.status === 201) {
                showSuccessToast('File(s) uploaded successfully.');
                resetForm();
                onClose();
                onUploaded();
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message || err?.message || 'Upload failed.';
            showErrorToast(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <CustomModal
            size="lg"
            show={show}
            setShow={handleSetShow}
            isHeaderHide={true}
            autoClose={false}
            enforceFocus={false}
        >
            <div className="patient-files__modal">
                <div className="patient-files__modal-head">
                    <h2 className="patient-files__modal-title">Upload file / Document</h2>
                    <button
                        type="button"
                        className="patient-files__modal-close"
                        onClick={() => handleSetShow(false)}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <form ref={formRef} className="patient-files__form" onSubmit={onSubmit}>
                    {missingContext && (
                        <div className="alert alert-warning py-1 px-2 small mb-2" role="alert">
                            Organization, location, patient, or visit is missing. Close and check your session.
                        </div>
                    )}

                    <div className="row g-2">
                        <div className="col-md-6">
                            <label className="patient-files__label" htmlFor="pf-title">
                                Title
                            </label>
                            <input
                                id="pf-title"
                                className="form-control form-control-sm patient-files__input"
                                type="text"
                                value={title}
                                onChange={(ev) => setTitle(ev.target.value)}
                                placeholder="e.g. CBC report, Chest X-ray"
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="patient-files__label" htmlFor="pf-category">
                                Category
                            </label>
                            <select
                                id="pf-category"
                                className="form-control form-control-sm patient-files__input"
                                value={category}
                                onChange={(ev) => setCategory(ev.target.value)}
                            >
                                {categoryOptions.map(([key, value]) => (
                                    <option key={value} value={value}>
                                        {key.replace(/_/g, ' ')}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="patient-files__label" htmlFor="pf-clinical">
                                Clinical type
                            </label>
                            <select
                                id="pf-clinical"
                                className="form-control form-control-sm patient-files__input"
                                value={clinicalType}
                                onChange={(ev) => setClinicalType(ev.target.value)}
                            >
                                {clinicalTypeOptions.map(([key, value]) => (
                                    <option key={value} value={value}>
                                        {key.replace(/_/g, ' ')}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-12">
                            <label className="patient-files__label" htmlFor="pf-desc">
                                Description
                            </label>
                            <textarea
                                id="pf-desc"
                                className="form-control form-control-sm patient-files__input"
                                rows={2}
                                value={description}
                                onChange={(ev) => setDescription(ev.target.value)}
                                placeholder="Optional notes"
                            />
                        </div>
                        <div className="col-12">
                            <label className="patient-files__label" htmlFor="pf-files">
                                Files <span className="text-danger">*</span>
                            </label>
                            <input
                                ref={fileInputRef}
                                id="pf-files"
                                className="form-control form-control-sm patient-files__input patient-files__file-input"
                                type="file"
                                accept="application/pdf,image/jpeg,image/png,image/jpg"
                                multiple
                                onChange={onFileChange}
                            />
                            <p className="patient-files__file-hint">
                                Up to {MAX_FILES_PER_UPLOAD} files. Choose several at once, or add one by one. PDF
                                or images (JPEG, PNG). Multiple images in one upload are merged into one PDF on
                                the server.
                            </p>

                            {fileList.length > 0 && (
                                <div className="patient-files__preview-grid">
                                    {fileList.map((f, i) => (
                                        <div
                                            key={`${f.name}-${f.size}-${f.lastModified}-${i}`}
                                            className="patient-files__preview-tile"
                                        >
                                            <button
                                                type="button"
                                                className="patient-files__preview-remove"
                                                onClick={() => removeFileAt(i)}
                                                aria-label={`Remove ${f.name}`}
                                            >
                                                <FontAwesomeIcon icon={faTimes} size="xs" />
                                            </button>
                                            <div className="patient-files__preview-thumb">
                                                {f.type === 'application/pdf' ? (
                                                    <FontAwesomeIcon icon={faFilePdf} className="patient-files__pdf-icon" />
                                                ) : f.type && f.type.startsWith('image/') && imageBlobUrls[i] ? (
                                                    <img src={imageBlobUrls[i]} alt="" />
                                                ) : (
                                                    <span className="patient-files__preview-fallback">File</span>
                                                )}
                                            </div>
                                            <div className="patient-files__preview-meta">
                                                <span className="patient-files__preview-name" title={f.name}>
                                                    {f.name}
                                                </span>
                                                <span className="patient-files__preview-size">
                                                    {formatFileSize(f.size)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="patient-files__modal-actions">
                        <button
                            type="button"
                            className="btn patient-files__btn-secondary"
                            onClick={() => handleSetShow(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn patient-files__btn-primary"
                            disabled={submitting || missingContext}
                        >
                            {submitting ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    Uploading…
                                </>
                            ) : (
                                `Upload${fileList.length > 1 ? ` (${fileList.length} files)` : ''}`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </CustomModal>
    );
};

export default PatientFilesUploadModal;
